import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { message, notification, Modal, Form, Input, Button, Space, Row, Col } from 'antd';
import { apiHelpers } from '../pages/DashboardUtils';
import apiClient from '../services/api';
import CustomModal from '../components/CustomModal';
import { useAuthStore } from '../stores/authStore';
import { useDoubleClickProtection } from '../hooks/useDoubleClickProtection';

// 协作库主题创建表单组件
const CreateCollaborativeTopicForm = ({ kb, parentTopic, onConfirm, onCancel }) => {
  const { t } = useTranslation(['dashboard', 'common', 'messages']);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);
      await onConfirm(values.name, values.description, values.tagnamelist);
    } catch (error) {
      console.error('[CreateCollaborativeTopicForm] 表单提交失败:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      style={{ width: '100%' }}
    >
      <div style={{ width: '100%' }}>
      <Row align="middle" gutter={0} style={{ width: '80%', margin: '0 auto 16px' }}>
        <Col span={5}>
          <div style={{ fontWeight: 500, textAlign: 'left' }}>
            {t('dashboard:topic_name', { defaultValue: 'Topic Name' })} <span style={{ color: '#ff4d4f' }}>*</span>
          </div>
        </Col>
        <Col span={19}>
          <Form.Item
            name="name"
            rules={[{ required: true, message: t('messages:please_enter_topic_name', { defaultValue: 'Please enter topic name' }) }]}
            style={{ marginBottom: 0 }}
          >
            <Input placeholder={t('dashboard:please_enter_topic_name', { defaultValue: 'Please enter topic name' })} />
          </Form.Item>
        </Col>
      </Row>
      
      <Row align="top" gutter={0} style={{ width: '80%', margin: '0 auto 16px' }}>
        <Col span={5}>
          <div style={{ fontWeight: 500, textAlign: 'left', lineHeight: '32px' }}>
            {t('dashboard:topic_description', { defaultValue: 'Topic Description' })} <span style={{ color: '#ff4d4f' }}>*</span>
          </div>
        </Col>
        <Col span={19}>
          <Form.Item
            name="description"
            rules={[
              { required: true, message: t('messages:please_enter_topic_description', { defaultValue: 'Please enter topic description' }) },
              { min: 20, message: t('messages:min_20_chars', { defaultValue: 'At least 20 characters' }) }
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input.TextArea 
              rows={3} 
              placeholder={t('dashboard:please_enter_topic_description_min', { defaultValue: 'Please enter topic description (at least 20 characters)' })} 
            />
          </Form.Item>
        </Col>
      </Row>
      
      <Row align="top" gutter={0} style={{ width: '80%', margin: '0 auto 16px' }}>
        <Col span={5}>
          <div style={{ fontWeight: 500, textAlign: 'left', lineHeight: '32px' }}>
            {t('dashboard:tag_list', { defaultValue: 'Tag List' })} <span style={{ color: '#ff4d4f' }}>*</span>
          </div>
        </Col>
        <Col span={19}>
          <Form.Item
            name="tagnamelist"
            rules={[
              { required: true, message: t('messages:please_enter_tag_list', { defaultValue: 'Please enter tag list' }) },
              { min: 20, message: t('messages:tag_list_min', { defaultValue: 'At least 20 characters, comma-separated' }) }
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input.TextArea 
              rows={3} 
              placeholder={t('dashboard:please_enter_tag_list_comma', { defaultValue: 'Please enter tag list, comma-separated (at least 20 characters)' })} 
            />
          </Form.Item>
        </Col>
      </Row>
      
      <div style={{ 
        marginBottom: '16px', 
        padding: '8px', 
        backgroundColor: '#f0f2f5', 
        borderRadius: '4px',
        width: '80%',
        margin: '0 auto 16px'
      }}>
        <div style={{ fontSize: '12px', color: '#666' }}>
          💡 {t('dashboard:topic_create_tip', { defaultValue: 'This topic will be created in collaborative KB "{{kbName}}"', kbName: kb?.name })}
          {parentTopic ? t('dashboard:as_subtopic_of', { defaultValue: ', as subtopic of "{{parentName}}"', parentName: parentTopic.name }) : t('dashboard:as_root_topic', { defaultValue: ', as root topic' })}
          {t('dashboard:all_members_access', { defaultValue: ', all collaborative KB members can access' })}
        </div>
      </div>
      
      <div style={{ textAlign: 'right', width: '80%', margin: '0 auto' }}>
        <Space>
          <Button onClick={onCancel}>{t('common:actions.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button 
            type="primary" 
            onClick={handleSubmit}
            loading={isSubmitting}
          >
            {t('dashboard:actions.create_topic', { defaultValue: 'Create Topic' })}
          </Button>
        </Space>
      </div>
    </div>
    </Form>
  );
};

const CollaborativeKnowledgeBaseContext = createContext();

export const useCollaborativeKnowledgeBase = () => {
  const context = useContext(CollaborativeKnowledgeBaseContext);
  if (!context) {
    throw new Error('useCollaborativeKnowledgeBase must be used within a CollaborativeKnowledgeBaseProvider');
  }
  return context;
};

export const CollaborativeKnowledgeBaseProvider = ({ children }) => {
  const { t } = useTranslation(['dashboard', 'common', 'messages']);
  
  // 获取当前用户信息
  const { user: currentUser } = useAuthStore();
  
  // Collaborative KB related state
  const [collaborativeKBs, setCollaborativeKBs] = useState([]);
  const [expandedCollaborativeKBsArray, setExpandedCollaborativeKBsArray] = useState([]);
  const [collaborativeKBTopics, setCollaborativeKBTopics] = useState({});
  const [isCreateCollaborativeVisible, setIsCreateCollaborativeVisible] = useState(false);
  const [createCollaborativeForm] = Form.useForm();
  const [selectedCollaborativeKB, setSelectedCollaborativeKB] = useState(null);
  const [editingCollaborativeKB, setEditingCollaborativeKB] = useState(null);
  const [editingCollaborativeKBName, setEditingCollaborativeKBName] = useState('');
  const [isCreateCollaborativeTopicVisible, setIsCreateCollaborativeTopicVisible] = useState(false);
  const [createCollaborativeTopicForm] = Form.useForm();
  const [isMembersVisible, setIsMembersVisible] = useState(false);
  const [selectedKBForMembers, setSelectedKBForMembers] = useState(null);
  const [kbMembers, setKbMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [topicNotesCount, setTopicNotesCount] = useState({});

  // 创建兼容的展开状态对象，提供 has 方法
  const expandedCollaborativeKBs = useMemo(() => ({
    has: (id) => expandedCollaborativeKBsArray.includes(id),
    add: (id) => setExpandedCollaborativeKBsArray(prev => prev.includes(id) ? prev : [...prev, id]),
    delete: (id) => setExpandedCollaborativeKBsArray(prev => prev.filter(item => item !== id)),
    clear: () => setExpandedCollaborativeKBsArray([]),
    size: expandedCollaborativeKBsArray.length,
    forEach: (callback) => expandedCollaborativeKBsArray.forEach(callback),
    values: () => expandedCollaborativeKBsArray.values(),
    entries: () => expandedCollaborativeKBsArray.map(item => [item, item]),
    keys: () => expandedCollaborativeKBsArray.values(),
    [Symbol.iterator]: function* () {
      yield* expandedCollaborativeKBsArray;
    }
  }), [expandedCollaborativeKBsArray]);
  
  // 自定义Modal状态
  const [isShareModalVisible, setIsShareModalVisible] = useState(false);
  const [shareModalData, setShareModalData] = useState(null);
  const [isLeaveModalVisible, setIsLeaveModalVisible] = useState(false);
  const [leaveModalData, setLeaveModalData] = useState(null);
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [renameModalData, setRenameModalData] = useState(null);
  // 协作库新建主题自定义Modal状态
  const [isCreateTopicModalVisible, setIsCreateTopicModalVisible] = useState(false);
  const [createTopicModalData, setCreateTopicModalData] = useState(null);
  
  // 防多次点击保护
  const createTopicProtection = useDoubleClickProtection(3000); // 3秒保护期
  const deleteTopicProtection = useDoubleClickProtection(2000); // 2秒保护期
  const leaveKBProtection = useDoubleClickProtection(2000); // 2秒保护期

  // 检查用户是否为协作库创建者
  const isCollaborativeKBCreator = useCallback((kb) => {
    if (!currentUser || !kb) {
      return false;
    }
    
    const isCreator = kb.user?.id === currentUser.id || kb.user_id === currentUser.id;
    return isCreator;
  }, [currentUser]);

  // 获取用户协作库列表
  const fetchUserCollaborativeKBs = useCallback(async () => {
    try {
      const kbs = await apiHelpers.fetchUserCollaborativeKBs();
      setCollaborativeKBs(kbs);
      console.log('设置协作库列表:', kbs);
      return kbs;
    } catch (error) {
      console.error('获取协作库列表失败:', error);
      throw error;
    }
  }, []);

  // 递归获取主题树中所有主题的笔记数量
  const fetchTopicNotesCountForTree = useCallback(async (topics) => {
    const allTopicIds = [];
    
    // 递归收集所有主题ID
    const collectTopicIds = (topicList) => {
      topicList.forEach(topic => {
        allTopicIds.push(topic.topic_id);
        if (topic.children && topic.children.length > 0) {
          collectTopicIds(topic.children);
        }
      });
    };
    
    collectTopicIds(topics);
    
    if (allTopicIds.length > 0) {
      console.log(`[前端] 开始获取 ${allTopicIds.length} 个主题的笔记数量...`);
      const countsMap = await apiHelpers.fetchMultipleTopicNotesCount(allTopicIds);
      setTopicNotesCount(prevCounts => ({
        ...prevCounts,
        ...countsMap
      }));
      console.log(`[前端] 主题笔记数量更新完成:`, countsMap);
    }
  }, []);

  // 获取协作库主题数据（当协作库展开时调用）
  const fetchCollaborativeKBTopics = useCallback(async (kbId) => {
    console.log('[前端] 获取协作库主题数据:', kbId);
    
    // 如果主题数据已存在，直接返回
    if (collaborativeKBTopics[kbId]) {
      console.log('[前端] 协作库主题数据已存在:', kbId, collaborativeKBTopics[kbId]);
      return collaborativeKBTopics[kbId];
    }
    
    // 获取主题树数据
    try {
      const topics = await apiHelpers.fetchCollaborativeKBTopics(kbId);
      console.log('[前端] 获取协作库主题数据成功:', kbId, topics);
      
      setCollaborativeKBTopics(prevTopics => ({
        ...prevTopics,
        [kbId]: topics
      }));
      
      // 获取该协作库所有主题的笔记数量
      if (topics && topics.length > 0) {
        await fetchTopicNotesCountForTree(topics);
      }
      
      return topics;
    } catch (err) {
      console.error('获取协作库主题失败:', err);
      return [];
    }
  }, [collaborativeKBTopics, fetchTopicNotesCountForTree]);

  // 计算协作库的总笔记数量
  const calculateCollaborativeKBNotesCount = useCallback((kbId) => {
    const topics = collaborativeKBTopics[kbId];
    if (!topics || topics.length === 0) {
      return 0;
    }

    // 只累加Level 0主题的笔记数量（从topicNotesCount缓存中获取）
    return topics.reduce((total, topic) => {
      return total + (topicNotesCount[topic.topic_id] || 0);
    }, 0);
  }, [collaborativeKBTopics, topicNotesCount]);

  /**
   * 开始创建协作库主题
   * 功能：设置创建主题的模态框数据并显示模态框
   * 参数：kb - 协作库对象
   * 修改时间：2025.10.19
   * 修改内容：添加防多次点击保护机制，解决createTopicModalData为null的问题
   */
  const handleStartCreateCollaborativeKBTopic = useCallback((kb) => {
    setCreateTopicModalData({
      kb,
      parentTopic: null
    });
    setIsCreateTopicModalVisible(true);
  }, []);

  // 创建协作库主题
  const handleCreateCollaborativeKBTopic = useCallback(async (values, selectedParentTopic, refreshKnowledgeBasesAndTopics) => {
    try {
      const result = await apiHelpers.createCollaborativeKBTopic(selectedCollaborativeKB.kb_id, {
        name: values.name,
        description: values.description,
        parentTopicId: selectedParentTopic ? selectedParentTopic.topic_id : null
      });
      
      const isSubTopic = selectedParentTopic !== null;
      message.success(t('messages:collaborative_topic_create_success', { defaultValue: 'Collaborative topic created successfully' }));
      setIsCreateCollaborativeTopicVisible(false);
      createCollaborativeTopicForm.resetFields();
      
      // 刷新笔记库和主题数据 - 优先使用传入的刷新函数，否则使用本地刷新
      if (refreshKnowledgeBasesAndTopics) {
        console.log('[handleCreateCollaborativeKBTopic] 调用refreshKnowledgeBasesAndTopics刷新数据');
        await refreshKnowledgeBasesAndTopics();
      } else {
        console.log('[handleCreateCollaborativeKBTopic] refreshKnowledgeBasesAndTopics不可用，调用本地刷新');
        // 回退到本地刷新
        await refreshCollaborativeKBData();
      }
      
      setSelectedCollaborativeKB(null);
      return result;
    } catch (error) {
      console.error('创建协作库主题失败:', error);
      message.error(t('messages:collaborative_topic_create_failed', { defaultValue: 'Failed to create collaborative topic' }));
      throw error;
    }
  }, [selectedCollaborativeKB, createCollaborativeTopicForm]);

  /**
   * 确认创建协作库主题
   * 功能：处理协作库主题的创建逻辑，包括API调用、数据刷新、防多次点击保护
   * 参数：newName - 主题名称, newDescription - 主题描述, newTagnamelist - 标签列表
   * 修改时间：2025.10.19
   * 修改内容：
   * 1. 添加防多次点击保护机制（3秒保护期）
   * 2. 添加createTopicModalData为null的恢复机制
   * 3. 强制刷新当前协作库主题数据
   * 4. 自动展开协作库以显示新主题
   * 5. 调用全局刷新函数确保数据同步
   */
  const handleConfirmCreateCollaborativeKBTopic = useCallback(async (newName, newDescription, newTagnamelist) => {
    console.log('[handleConfirmCreateCollaborativeKBTopic] 函数开始执行');
    console.log('[handleConfirmCreateCollaborativeKBTopic] 参数:', { newName, newDescription, newTagnamelist });
    console.log('[handleConfirmCreateCollaborativeKBTopic] createTopicModalData:', createTopicModalData);
    
    // 防多次点击保护
    if (!createTopicProtection.protect()) {
      console.log('[handleConfirmCreateCollaborativeKBTopic] 操作被保护，防止多次点击');
      message.warning('Please do not click repeatedly, try again later');
      return;
    }

    // 检查createTopicModalData是否为null
    if (!createTopicModalData || !createTopicModalData.kb) {
      console.error('[handleConfirmCreateCollaborativeKBTopic] createTopicModalData为null或缺少kb信息:', createTopicModalData);
      console.log('[handleConfirmCreateCollaborativeKBTopic] 尝试从协作库列表中获取当前协作库信息...');

      // 尝试从协作库列表中获取当前协作库信息
      const currentKB = collaborativeKBs.find(kb => kb.kb_id === '0734667b-dccb-4e28-9191-4600c722b6b6'); // 从日志中获取的kbId
      if (currentKB) {
        console.log('[handleConfirmCreateCollaborativeKBTopic] 找到当前协作库:', currentKB);
        // 重新设置createTopicModalData
        setCreateTopicModalData({
          kb: currentKB,
          parentTopic: null
        });
        // 继续执行
      } else {
        console.error('[handleConfirmCreateCollaborativeKBTopic] 无法找到当前协作库信息');
        message.error('Failed to create topic: missing collaborative KB information');
        return;
      }
    }
    
    try {
      console.log('创建协作库主题:', { 
        kbId: createTopicModalData.kb.kb_id, 
        name: newName, 
        description: newDescription,
        tagnamelist: newTagnamelist
      });
      
      // 保存kb信息，因为后续会清空createTopicModalData
      const kbId = createTopicModalData.kb.kb_id;
      
      // 调用协作库主题创建API
      console.log('[handleConfirmCreateCollaborativeKBTopic] 开始调用API创建主题...');
      const result = await apiHelpers.createCollaborativeKBTopic(kbId, {
        name: newName,
        description: newDescription,
        tagnamelist: newTagnamelist,
        parentTopicId: createTopicModalData.parentTopic ? createTopicModalData.parentTopic.topic_id : null
      });
      console.log('[handleConfirmCreateCollaborativeKBTopic] API调用成功，结果:', result);
      
      message.success(t('messages:collaborative_topic_create_success', { defaultValue: 'Collaborative topic created successfully' }));
      console.log('[handleConfirmCreateCollaborativeKBTopic] 主题创建成功，开始刷新数据...');
      console.log('[handleConfirmCreateCollaborativeKBTopic] 准备清空模态框状态...');
      setIsCreateTopicModalVisible(false);
      setCreateTopicModalData(null);
      
      // 强制刷新当前协作库的主题数据
      try {
        console.log('[handleConfirmCreateCollaborativeKBTopic] 强制刷新当前协作库主题数据:', kbId);
        const updatedTopics = await apiHelpers.fetchCollaborativeKBTopics(kbId);
        setCollaborativeKBTopics(prev => {
          const newTopics = {
            ...prev,
            [kbId]: updatedTopics
          };
          console.log('[handleConfirmCreateCollaborativeKBTopic] 更新协作库主题数据:', {
            kbId: kbId,
            oldTopics: prev[kbId]?.length || 0,
            newTopics: updatedTopics?.length || 0,
            allKBs: Object.keys(newTopics)
          });
          return newTopics;
        });
        console.log('[handleConfirmCreateCollaborativeKBTopic] 当前协作库主题数据已更新:', updatedTopics);
        
        // 同时更新主题笔记数量统计
        if (updatedTopics && updatedTopics.length > 0) {
          await fetchTopicNotesCountForTree(updatedTopics);
        }
      } catch (error) {
        console.error('[handleConfirmCreateCollaborativeKBTopic] 刷新当前协作库主题数据失败:', error);
      }
      
      // 确保协作库处于展开状态，以便显示新创建的主题
      try {
        console.log('[handleConfirmCreateCollaborativeKBTopic] 确保协作库展开状态:', kbId);
        if (!expandedCollaborativeKBs.has(kbId)) {
          console.log('[handleConfirmCreateCollaborativeKBTopic] 协作库未展开，自动展开:', kbId);
          setExpandedCollaborativeKBsArray(prev => {
            if (!prev.includes(kbId)) {
              const newExpanded = [...prev, kbId];
              console.log('[handleConfirmCreateCollaborativeKBTopic] 更新展开状态:', {
                old: prev,
                new: newExpanded
              });
              return newExpanded;
            }
            return prev;
          });
        } else {
          console.log('[handleConfirmCreateCollaborativeKBTopic] 协作库已展开:', kbId);
        }
      } catch (error) {
        console.error('[handleConfirmCreateCollaborativeKBTopic] 设置协作库展开状态失败:', error);
      }
      
      // 刷新笔记库和主题数据 - 优先使用全局刷新函数，否则使用本地刷新
      if (window.refreshKnowledgeBasesAndTopics) {
        console.log('[handleConfirmCreateCollaborativeKBTopic] 调用全局refreshKnowledgeBasesAndTopics刷新数据');
        await window.refreshKnowledgeBasesAndTopics();
      } else {
        console.log('[handleConfirmCreateCollaborativeKBTopic] 全局刷新函数不可用，调用本地刷新');
        // 回退到本地刷新
        await refreshCollaborativeKBData();
      }
      
      return result;
    } catch (error) {
      console.error('创建协作库主题失败:', error);
      console.error('错误详情:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data
      });
      message.error(t('messages:collaborative_topic_create_failed_with_error', { defaultValue: 'Failed to create collaborative topic: {{error}}', error: error.response?.data?.message || error.message }));
      // 发生错误时释放保护
      createTopicProtection.release();
    }
  }, [expandedCollaborativeKBs, setExpandedCollaborativeKBsArray, createTopicProtection]);

  // 取消创建协作库主题
  const handleCancelCreateCollaborativeKBTopic = useCallback(() => {
    setIsCreateTopicModalVisible(false);
    setCreateTopicModalData(null);
    // 取消时释放保护
    createTopicProtection.release();
  }, [createTopicProtection]);

  /**
   * 删除协作库主题
   * 功能：处理协作库主题的删除逻辑，包括确认对话框、API调用、数据刷新、防多次点击保护
   * 参数：kbId - 协作库ID, topicId - 主题ID, topicName - 主题名称, refreshKnowledgeBasesAndTopics - 刷新函数
   * 修改时间：2025.10.19
   * 修改内容：
   * 1. 添加防多次点击保护机制（2秒保护期）
   * 2. 优化错误处理和日志记录
   * 3. 确保删除后数据正确刷新
   */
  const handleDeleteCollaborativeTopic = useCallback(async (kbId, topicId, topicName, refreshKnowledgeBasesAndTopics) => {
    // 防多次点击保护
    if (!deleteTopicProtection.protect()) {
      console.log('[handleDeleteCollaborativeTopic] 操作被保护，防止多次点击');
      message.warning('Please do not click repeatedly, try again later');
      return;
    }

    try {
      console.log('[handleDeleteCollaborativeTopic] 开始删除协作库主题:', { kbId, topicId, topicName });
      console.log('[handleDeleteCollaborativeTopic] apiHelpers对象:', typeof apiHelpers, apiHelpers);
      console.log('[handleDeleteCollaborativeTopic] deleteCollaborativeKBTopic方法:', typeof apiHelpers?.deleteCollaborativeKBTopic);
      
      // 显示确认对话框
      console.log('[handleDeleteCollaborativeTopic] 准备显示确认对话框');
      
      // 使用window.confirm作为备选方案
      const confirmed = window.confirm(`Are you sure you want to delete topic "${topicName}"? This action cannot be undone.`);
      console.log('[handleDeleteCollaborativeTopic] window.confirm结果:', confirmed);
      
      if (!confirmed) {
        console.log('[handleDeleteCollaborativeTopic] 用户取消删除');
        return; // 直接返回，不执行删除操作
      }
      
      console.log('[handleDeleteCollaborativeTopic] 用户确认删除，开始调用API');
      
      console.log('[handleDeleteCollaborativeTopic] 用户确认删除，开始调用API');
      console.log('[handleDeleteCollaborativeTopic] 调用apiHelpers.deleteCollaborativeKBTopic:', { kbId, topicId });
      
      // 调用删除API
      const result = await apiHelpers.deleteCollaborativeKBTopic(kbId, topicId);
      console.log('[handleDeleteCollaborativeTopic] API调用成功:', result);
      
      message.success(t('messages:collaborative_topic_delete_success', { defaultValue: 'Collaborative topic deleted successfully' }));
      
      // 强制清除当前协作库的主题数据缓存
      setCollaborativeKBTopics(prev => {
        const newTopics = { ...prev };
        delete newTopics[kbId];
        return newTopics;
      });
      
      // 重新获取协作库主题数据以确保数据同步
      try {
        console.log('[handleDeleteCollaborativeTopic] 重新获取协作库主题数据:', kbId);
        const updatedTopics = await apiHelpers.fetchCollaborativeKBTopics(kbId);
        setCollaborativeKBTopics(prev => {
          const newTopics = {
            ...prev,
            [kbId]: updatedTopics
          };
          console.log('[handleDeleteCollaborativeTopic] 更新协作库主题数据:', {
            kbId: kbId,
            oldTopics: prev[kbId]?.length || 0,
            newTopics: updatedTopics?.length || 0
          });
          return newTopics;
        });
        
        // 同时更新主题笔记数量统计
        if (updatedTopics && updatedTopics.length > 0) {
          await fetchTopicNotesCountForTree(updatedTopics);
        }
      } catch (error) {
        console.error('[handleDeleteCollaborativeTopic] 重新获取协作库主题数据失败:', error);
      }
      
      // 刷新数据 - 优先使用传入的刷新函数，否则使用本地刷新
      if (refreshKnowledgeBasesAndTopics) {
        console.log('[handleDeleteCollaborativeTopic] 调用refreshKnowledgeBasesAndTopics刷新数据');
        await refreshKnowledgeBasesAndTopics();
        console.log('[handleDeleteCollaborativeTopic] 数据刷新完成');
      } else {
        console.log('[handleDeleteCollaborativeTopic] refreshKnowledgeBasesAndTopics不可用，调用本地刷新');
        // 回退到本地刷新
        await refreshCollaborativeKBData();
      }
    } catch (error) {
      console.log('[handleDeleteCollaborativeTopic] 捕获到错误:', error);
      console.error('[handleDeleteCollaborativeTopic] 删除协作库主题失败:', error);
      console.error('[handleDeleteCollaborativeTopic] 错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      
      // 检查是否是403错误（缺省主题不能删除）
      if (error.response?.status === 403) {
        const errorMessage = error.response?.data?.message || 'default topic cannot be deleted';
        
        // 使用多种方式显示错误信息，确保用户能看到
        console.error('[handleDeleteCollaborativeTopic] 403错误，显示错误信息:', errorMessage);
        
        // 方案1：使用notification.error
        //notification.error({
        //  message: t('messages:collaborative_default_topic_delete_failed', { defaultValue: 'Failed to delete collaborative default topic' }),
        //  description: errorMessage,
        //  duration: 5,
        //  placement: 'top'
        //});
        
        // 方案2：使用alert作为备选
        setTimeout(() => {
          alert(`Failed to delete topic:${errorMessage}`);
        }, 100);
        
        // 方案3：使用message.error作为备选
        //message.error(errorMessage);
        
      } else {
        // 其他错误使用普通消息提示
        message.error(t('messages:collaborative_topic_delete_failed', { defaultValue: 'Failed to delete collaborative topic' }));
      }
      
      // 发生错误时释放保护
      deleteTopicProtection.release();
    }
  }, [deleteTopicProtection]);

  // 开始重命名协作库
  const handleRenameCollaborativeKB = useCallback((kb) => {
    console.log('[重命名协作库] 开始重命名:', kb);
    
    // 设置重命名Modal数据
    setRenameModalData({
      kb,
      name: kb.name,
      description: kb.description || ''
    });
    setIsRenameModalVisible(true);
  }, []);

  // 确认重命名协作库
  const handleConfirmRenameCollaborativeKB = useCallback(async (kbId, newName, newDescription) => {
    try {
      console.log('重命名协作库:', { kbId, newName, newDescription });
      
      // 调用协作库重命名API
      const response = await apiClient.put(`/users/knowledge-base/collaborative/${kbId}`, {
        name: newName,
        description: newDescription || ''
      });
      
      if (response.data && response.data.success !== false) {
        // 更新本地状态
        setCollaborativeKBs(prev => 
          prev.map(kb => 
            kb.kb_id === kbId ? { ...kb, name: newName, description: newDescription || kb.description } : kb
          )
        );
        
        message.success(t('messages:collaborative_kb_rename_success', { defaultValue: 'Collaborative KB renamed successfully' }));
        setIsRenameModalVisible(false);
        setRenameModalData(null);
      } else {
        message.error(t('messages:collaborative_kb_rename_failed_format', { defaultValue: 'Failed to rename collaborative KB: Invalid response format' }));
      }
    } catch (error) {
      console.error('重命名协作库错误:', error);
      message.error(t('messages:collaborative_kb_rename_failed_with_error', { defaultValue: 'Failed to rename collaborative KB: {{error}}', error: error.response?.data?.message || error.message }));
    }
  }, []);

  // 取消重命名协作库
  const handleCancelRenameCollaborativeKB = useCallback(() => {
    setIsRenameModalVisible(false);
    setRenameModalData(null);
  }, []);

  // 分享协作库
  const handleShareCollaborativeKB = useCallback(async (kb) => {
    try {
      console.log('[分享协作库] 开始处理分享请求:', kb);
      console.log('[分享协作库] 当前用户:', currentUser);
      console.log('[分享协作库] 协作库创建者:', kb.user);
      
      if (!isCollaborativeKBCreator(kb)) {
        console.log('[分享协作库] 权限检查失败，用户不是创建者');
        // 显示权限不足的模态框
        setShareModalData({
          kb,
          shareUrl: null,
          isPermissionDenied: true
        });
        setIsShareModalVisible(true);
        return;
      }

      console.log('[分享协作库] 开始为协作库生成分享链接:', kb.kb_id);
      
      // 调用API生成分享链接
      const response = await apiClient.post(`/users/knowledge-base/collaborative/${kb.kb_id}/share`);
      
      if (response.data && response.data.data) {
        const { shareUrl } = response.data.data;
        const fullShareUrl = `${window.location.origin}${shareUrl}`;
        
        // 设置分享Modal数据
        setShareModalData({
          kb,
          shareUrl: fullShareUrl
        });
        setIsShareModalVisible(true);
        
        console.log('[分享协作库] 分享链接:', fullShareUrl);
        message.success(t('messages:share_link_generated', { defaultValue: 'Share link generated successfully' }));
      } else {
        console.error('[分享协作库] 响应数据格式错误:', response.data);
        message.error(t('messages:share_link_generate_failed', { defaultValue: 'Failed to generate share link' }));
      }
    } catch (error) {
      console.error('[分享协作库] 分享协作库失败:', error);
      message.error(t('messages:share_collaborative_kb_failed_with_error', { defaultValue: 'Failed to share collaborative KB: {{error}}', error: error.response?.data?.message || error.message }));
    }
  }, [isCollaborativeKBCreator]);

  // 关闭协作库分享
  const handleDisableCollaborativeKBShare = useCallback(async (kbId) => {
    try {
      // 权限检查：只有创建者才能关闭分享
      const kb = collaborativeKBs.find(k => k.kb_id === kbId);
      if (!kb || !isCollaborativeKBCreator(kb)) {
        message.error(t('messages:only_creator_can_disable_share', { defaultValue: 'Only the collaborative KB creator can disable sharing' }));
        return;
      }

      console.log('[关闭分享] 开始关闭协作库分享:', kbId);
      
      // 调用API关闭分享
      const response = await apiClient.delete(`/users/knowledge-base/collaborative/${kbId}/share`);
      
      if (response.data && response.data.success !== false) {
        message.success(t('messages:share_disabled', { defaultValue: 'Sharing has been disabled' }));
        // 关闭分享模态框
        setIsShareModalVisible(false);
        setShareModalData(null);
      } else {
        console.error('[关闭分享] 响应数据格式错误:', response.data);
        message.error(t('messages:disable_share_failed', { defaultValue: 'Failed to disable sharing' }));
      }
    } catch (error) {
      console.error('[关闭分享] 关闭分享失败:', error);
      message.error(t('messages:disable_share_failed_with_error', { defaultValue: 'Failed to disable sharing: {{error}}', error: error.response?.data?.message || error.message }));
    }
  }, [collaborativeKBs, isCollaborativeKBCreator]);

  // 创建协作库
  const handleCreateCollaborative = useCallback(async (values, refreshKnowledgeBasesAndTopics) => {
    try {
      console.log('Creating collaborative KB:', values);
      
      // Call API to create collaborative KB
      const response = await apiClient.post('/users/knowledge-base/collaborative', {
        name: values.name,
        description: values.description || ''
      });
      
      console.log('Collaborative KB created successfully:', response.data);
      message.success(t('messages:collaborative_kb_create_success', { defaultValue: 'Collaborative KB created successfully' }));
      
      // Close modal and reset form
      setIsCreateCollaborativeVisible(false);
      createCollaborativeForm.resetFields();
      
      // Refresh collaborative KB list
      await fetchUserCollaborativeKBs();
      
      // Refresh knowledge bases and topics if provided
      if (refreshKnowledgeBasesAndTopics) {
        await refreshKnowledgeBasesAndTopics();
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to create collaborative KB:', error);
      message.error(t('messages:collaborative_kb_create_failed_with_error', { defaultValue: 'Failed to create collaborative KB: {{error}}', error: error.response?.data?.message || error.message }));
      throw error;
    }
  }, [createCollaborativeForm, fetchUserCollaborativeKBs]);

  // 查看协作库成员
  const handleViewMembers = useCallback(async (kb) => {
    setSelectedKBForMembers(kb);
    setIsMembersVisible(true);
    setLoadingMembers(true);
    
    try {
      const members = await apiHelpers.fetchCollaborativeKBMembers(kb.kb_id);
      setKbMembers(members);
      console.log('[查看成员] 成功获取协作库成员:', members);
    } catch (error) {
      console.error('[查看成员] 获取协作库成员失败:', error);
      message.error(t('messages:get_collaborative_kb_members_failed', { defaultValue: 'Failed to get collaborative KB members' }));
      setKbMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  // 刷新协作库数据
  const refreshCollaborativeKBData = useCallback(async () => {
    try {
      console.log('[CollaborativeKnowledgeBaseContext] 开始刷新协作库数据...');
      
      // 刷新协作库列表
      await fetchUserCollaborativeKBs();
      
      // 刷新已展开的协作库的主题数据
      const refreshPromises = [];
      for (const kb of collaborativeKBs) {
        if (expandedCollaborativeKBs.has(kb.kb_id)) {
          refreshPromises.push(
            apiHelpers.fetchCollaborativeKBTopics(kb.kb_id)
              .then(topics => ({
                kbId: kb.kb_id,
                topics,
                success: true
              }))
              .catch(error => {
                console.error(`刷新协作库 ${kb.kb_id} 主题数据失败:`, error);
                return {
                  kbId: kb.kb_id,
                  topics: [],
                  success: false,
                  error
                };
              })
          );
        }
      }
      
      if (refreshPromises.length > 0) {
        console.log(`[CollaborativeKnowledgeBaseContext] 开始刷新 ${refreshPromises.length} 个已展开协作库的主题数据...`);
        const collaborativeTopicsResults = await Promise.all(refreshPromises);
        
        // 批量更新协作库主题数据
        setCollaborativeKBTopics(prev => {
          const newTopics = { ...prev };
          collaborativeTopicsResults.forEach(result => {
            newTopics[result.kbId] = result.topics;
            console.log(`[CollaborativeKnowledgeBaseContext] 协作库 ${result.kbId} 主题数据更新:`, {
              success: result.success,
              topics: result.topics?.length || 0
            });
          });
          return newTopics;
        });
      }
      
      console.log('[CollaborativeKnowledgeBaseContext] 协作库数据刷新完成');
    } catch (error) {
      console.error('[CollaborativeKnowledgeBaseContext] 刷新协作库数据失败:', error);
    }
  }, [collaborativeKBs, expandedCollaborativeKBs, fetchUserCollaborativeKBs]);

  // 将刷新函数暴露到全局，供Dashboard调用
  React.useEffect(() => {
    window.refreshCollaborativeKBData = refreshCollaborativeKBData;
    
    // 清理函数
    return () => {
      delete window.refreshCollaborativeKBData;
    };
  }, [refreshCollaborativeKBData]);

  // 关闭协作库
  const handleLeaveCollaborativeKB = useCallback(async (kb) => {
    try {
      console.log('[关闭协作库] 开始关闭协作库:', kb);
      console.log('[关闭协作库] kb.kb_id:', kb.kb_id);
      console.log('[关闭协作库] kb object keys:', Object.keys(kb));
      
      // 设置关闭Modal数据
      setLeaveModalData({
        kb
      });
      setIsLeaveModalVisible(true);
      
    } catch (error) {
      console.error('[关闭协作库] 关闭协作库失败:', error);
      message.error(t('messages:close_collaborative_kb_failed_with_error', { defaultValue: 'Failed to close collaborative KB: {{error}}', error: error.response?.data?.message || error.message }));
    }
  }, []);

  /**
   * 确认退出协作库
   * 功能：处理用户退出协作库的逻辑，包括API调用、错误处理、防多次点击保护
   * 参数：无（使用leaveModalData中的协作库信息）
   * 修改时间：2025.10.19
   * 修改内容：
   * 1. 添加防多次点击保护机制（2秒保护期）
   * 2. 优化错误处理和日志记录
   * 3. 修复创建者不能退出的错误显示问题
   * 4. 添加详细的API调用日志
   */
  const handleConfirmLeaveCollaborativeKB = useCallback(async () => {
    // 防多次点击保护
    if (!leaveKBProtection.protect()) {
      message.warning('Please do not click repeatedly, try again later');
      return;
    }

    try {
      if (!leaveModalData) {
        console.error('[退出协作库] leaveModalData is null or undefined');
        leaveKBProtection.release(); // 释放保护
        return;
      }
      
      
      if (!leaveModalData.kb || !leaveModalData.kb.kb_id) {
        console.error('[退出协作库] kb or kb.kb_id is missing');
        message.error(t('messages:leave_collaborative_kb_failed_missing_info', { defaultValue: 'Failed to leave collaborative KB: Missing necessary KB information' }));
        leaveKBProtection.release(); // 释放保护
        return;
      }
      
      // 调用退出协作库API
      const response = await apiClient.delete(`/users/knowledge-base/collaborative/${leaveModalData.kb.kb_id}/leave`);
      
      // 检查API响应结构
      if (response.data && response.data.data && response.data.data.success) {
        // 成功退出：从本地列表中移除该协作库
        setCollaborativeKBs(prev => 
          prev.filter(kb => kb.kb_id !== leaveModalData.kb.kb_id)
        );
        
        message.success(t('messages:leave_collaborative_kb_success', { defaultValue: 'Successfully left collaborative KB' }));
        setIsLeaveModalVisible(false);
        setLeaveModalData(null);
      } else if (response.data && response.data.message) {
        // 业务逻辑错误：显示后端返回的错误消息
        console.error('[退出协作库] 业务逻辑错误:', response.data.message);
        
        // 关闭当前模态框
        setIsLeaveModalVisible(false);
        setLeaveModalData(null);
        
        // 延迟显示错误，确保模态框已关闭
        setTimeout(() => {
          // 使用 alert 作为最可靠的显示方式
          alert(`Failed to Leave Collaborative KB\n\n${response.data.message}`);
          
          // 同时尝试 notification
          notification.error({
            message: 'Failed to Leave Collaborative KB',
            description: response.data.message,
            duration: 8,
            placement: 'topRight',
          });
          
          // 尝试 message
          //message.error(response.data.message);
        }, 100);
        
        leaveKBProtection.release(); // 释放保护
      } else {
        // 其他错误情况
        const errorMessage = response.data?.data?.message || response.data?.message || t('messages:leave_collaborative_kb_failed', { defaultValue: 'Failed to leave collaborative KB' });
        message.error(errorMessage);
        leaveKBProtection.release(); // 释放保护
      }
      
    } catch (error) {
      console.error('[退出协作库] 退出协作库错误:', error);
      message.error(t('messages:leave_collaborative_kb_failed_with_error', { defaultValue: 'Failed to leave collaborative KB: {{error}}', error: error.response?.data?.message || error.message }));
      leaveKBProtection.release(); // 释放保护
    }
  }, [leaveModalData, leaveKBProtection]);

  const value = {
    // State
    collaborativeKBs,
    expandedCollaborativeKBs,
    collaborativeKBTopics,
    isCreateCollaborativeVisible,
    createCollaborativeForm,
    selectedCollaborativeKB,
    editingCollaborativeKB,
    editingCollaborativeKBName,
    isCreateCollaborativeTopicVisible,
    createCollaborativeTopicForm,
    isMembersVisible,
    selectedKBForMembers,
    kbMembers,
    loadingMembers,
    topicNotesCount,
    
    // 自定义Modal状态
    isShareModalVisible,
    shareModalData,
    isLeaveModalVisible,
    leaveModalData,
    isRenameModalVisible,
    renameModalData,
    // 协作库新建主题自定义Modal状态
    isCreateTopicModalVisible,
    createTopicModalData,
    
    // State setters
    setCollaborativeKBs,
    setExpandedCollaborativeKBsArray,
    setCollaborativeKBTopics,
    setIsCreateCollaborativeVisible,
    setSelectedCollaborativeKB,
    setEditingCollaborativeKB,
    setEditingCollaborativeKBName,
    setIsCreateCollaborativeTopicVisible,
    setIsMembersVisible,
    setSelectedKBForMembers,
    setKbMembers,
    setLoadingMembers,
    setTopicNotesCount,
    
    // 自定义Modal状态setters
    setIsShareModalVisible,
    setShareModalData,
    setIsLeaveModalVisible,
    setLeaveModalData,
    setIsRenameModalVisible,
    setRenameModalData,
    // 协作库新建主题自定义Modal状态setters
    setIsCreateTopicModalVisible,
    setCreateTopicModalData,
    
    // Functions
    fetchUserCollaborativeKBs,
    fetchTopicNotesCountForTree,
    fetchCollaborativeKBTopics,
    calculateCollaborativeKBNotesCount,
    isCollaborativeKBCreator,
    handleStartCreateCollaborativeKBTopic,
    handleCreateCollaborativeKBTopic,
    handleCancelCreateCollaborativeKBTopic,
    handleDeleteCollaborativeTopic,
    handleRenameCollaborativeKB,
          handleConfirmRenameCollaborativeKB,
      handleCancelRenameCollaborativeKB,
      handleShareCollaborativeKB,
      handleDisableCollaborativeKBShare,
      handleCreateCollaborative,
      handleViewMembers,
      refreshCollaborativeKBData,
      handleConfirmCreateCollaborativeKBTopic,
      handleCancelCreateCollaborativeKBTopic,
    handleLeaveCollaborativeKB,
    handleConfirmLeaveCollaborativeKB
  };

  return (
    <CollaborativeKnowledgeBaseContext.Provider value={value}>
      {children}
      
      {/* 分享协作库Modal */}
      <CustomModal
        isOpen={isShareModalVisible}
        onClose={() => setIsShareModalVisible(false)}
        title={shareModalData?.isPermissionDenied ? t('dashboard:permission_denied', { defaultValue: 'Permission Denied' }) : t('dashboard:collaborative_kb_share_link', { defaultValue: 'Collaborative KB Share Link' })}
        width={600}
        showCancel={false}
        confirmText={t('common:actions.close', { defaultValue: 'Close' })}
        onConfirm={() => setIsShareModalVisible(false)}
      >
        {shareModalData && (
          <div>
            {shareModalData.isPermissionDenied ? (
              <div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: 16,
                  padding: '12px',
                  backgroundColor: '#fff2e8',
                  border: '1px solid #ffd591',
                  borderRadius: '6px'
                }}>
                  <span style={{ 
                    fontSize: '16px', 
                    marginRight: '8px',
                    color: '#fa8c16'
                  }}>
                    ⚠️
                  </span>
                  <span style={{ color: '#d46b08' }}>
                    {t('dashboard:permission_denied', { defaultValue: 'Permission Denied' })}
                  </span>
                </div>
                <p style={{ color: '#666', lineHeight: '1.6' }}>
                  {t('dashboard:only_creator_can_share', { defaultValue: 'Only the collaborative KB creator can share the KB.' })}
                </p>
                <p style={{ color: '#999', fontSize: '14px', marginTop: '8px' }}>
                  {t('dashboard:contact_creator_to_share', { defaultValue: 'If you need to share this collaborative KB, please contact the creator.' })}
                </p>
              </div>
            ) : (
              <div>
                <p>{t('dashboard:share_link_generated_others_can_join', { defaultValue: 'Share link has been generated, others can join the collaborative KB through this link:' })}</p>
                <Input.TextArea
                  value={shareModalData.shareUrl}
                  readOnly
                  rows={3}
                  style={{ marginBottom: 16 }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    type="primary"
                    onClick={() => {
                      navigator.clipboard.writeText(shareModalData.shareUrl);
                      message.success(t('messages:link_copied_to_clipboard', { defaultValue: 'Link copied to clipboard' }));
                    }}
                    style={{ marginRight: 8 }}
                  >
                    {t('dashboard:actions.copy_link', { defaultValue: 'Copy Link' })}
                  </Button>
                  {isCollaborativeKBCreator(shareModalData.kb) && (
                    <Button
                      danger
                      onClick={() => {
                        if (window.confirm(t('dashboard:confirm_disable_share', { defaultValue: 'Are you sure you want to disable sharing for this collaborative KB? After disabling, others will not be able to join through the share link.' }))) {
                          handleDisableCollaborativeKBShare(shareModalData.kb.kb_id);
                        }
                      }}
                    >
                      {t('dashboard:actions.disable_share', { defaultValue: 'Disable Share' })}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CustomModal>
      
      {/* 退出协作库Modal */}
      <CustomModal
        isOpen={isLeaveModalVisible}
        onClose={() => setIsLeaveModalVisible(false)}
        title={t('dashboard:confirm_leave_collaborative_kb', { defaultValue: 'Confirm Leave Collaborative KB' })}
        width={500}
        type="danger"
        confirmText={t('dashboard:actions.confirm_leave', { defaultValue: 'Confirm Leave' })}
        cancelText={t('common:actions.cancel', { defaultValue: 'Cancel' })}
        onConfirm={handleConfirmLeaveCollaborativeKB}
        onCancel={() => setIsLeaveModalVisible(false)}
      >
        {leaveModalData && (
          <div>
            <p>{t('dashboard:confirm_leave_kb_message', { defaultValue: 'Are you sure you want to leave collaborative KB "{{kbName}}"?', kbName: leaveModalData.kb.name })}</p>
            <p style={{ color: '#ff4d4f', fontSize: '14px' }}>
              {t('dashboard:leave_kb_warning', { defaultValue: 'After leaving, you will not be able to access the KB content, but other members can continue to use it.' })}
            </p>
          </div>
        )}
      </CustomModal>

      {/* 重命名协作库Modal */}
      <CustomModal
        isOpen={isRenameModalVisible}
        onClose={handleCancelRenameCollaborativeKB}
        title={t('dashboard:rename_collaborative_kb', { defaultValue: 'Rename Collaborative KB' })}
        width={600}
        confirmText={t('dashboard:actions.save_changes', { defaultValue: 'Save Changes' })}
        cancelText={t('common:actions.cancel', { defaultValue: 'Cancel' })}
        onConfirm={() => {
          if (renameModalData) {
            handleConfirmRenameCollaborativeKB(
              renameModalData.kb.kb_id, 
              renameModalData.name,
              renameModalData.description
            );
          }
        }}
        onCancel={handleCancelRenameCollaborativeKB}
      >
        {renameModalData && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                {t('dashboard:collaborative_kb_name', { defaultValue: 'Collaborative KB Name' })} *
              </label>
              <Input
                value={renameModalData.name}
                onChange={(e) => setRenameModalData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('dashboard:please_enter_collaborative_kb_name', { defaultValue: 'Please enter collaborative KB name' })}
              />
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                {t('dashboard:collaborative_kb_description', { defaultValue: 'Collaborative KB Description' })}
              </label>
              <Input.TextArea
                value={renameModalData.description}
                onChange={(e) => setRenameModalData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('dashboard:please_enter_collaborative_kb_description_optional', { defaultValue: 'Please enter collaborative KB description (optional)' })}
                rows={3}
              />
            </div>
            
            <div style={{ 
              marginBottom: '16px', 
              padding: '8px', 
              backgroundColor: '#f0f2f5', 
              borderRadius: '4px' 
            }}>
              <div style={{ fontSize: '12px', color: '#666' }}>
                💡 {t('dashboard:rename_kb_tip', { defaultValue: 'After modifying the collaborative KB name and description, all members will see the updates' })}
              </div>
            </div>
          </div>
        )}
      </CustomModal>

      {/* 新建协作库主题Modal */}
      <CustomModal
        isOpen={isCreateTopicModalVisible}
        onClose={handleCancelCreateCollaborativeKBTopic}
        title={t('dashboard:create_collaborative_topic', { defaultValue: 'Create Collaborative Topic' })}
        width={600}
        showCancel={false}
        showConfirm={false}
      >
        {createTopicModalData && (
          <CreateCollaborativeTopicForm
            kb={createTopicModalData.kb}
            parentTopic={createTopicModalData.parentTopic}
            onConfirm={handleConfirmCreateCollaborativeKBTopic}
            onCancel={handleCancelCreateCollaborativeKBTopic}
          />
        )}
      </CustomModal>
    </CollaborativeKnowledgeBaseContext.Provider>
  );
};